import { useState, useEffect, useRef, useCallback } from 'react'
import { Container, Card, Input, Button } from '../components/ui'
import FilterModal, { FilterState } from '../components/FilterModal'
import { getPosts, Post, PostFilters, deletePosts } from '../services/posts'

type SortBy = 'reply_count' | 'repost_count' | 'like_count' | 'quote_count' | 'post_created_at'

const POSTS_PER_PAGE = 20

export default function Dashboard() {
  const [posts, setPosts] = useState<Post[]>([])
  const [loading, setLoading] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [selectedPosts, setSelectedPosts] = useState<Set<number>>(new Set())
  const [sortBy, setSortBy] = useState<SortBy>('post_created_at')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc')
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const [filters, setFilters] = useState<FilterState>({
    dateFrom: '',
    dateTill: '',
    dayOfWeek: {
      mon: true,
      tue: true,
      wed: true,
      thu: true,
      fri: true,
      sat: true,
      sun: true,
    },
    postTypes: {
      posts: true,
      reposts: true,
      replays: true,
    },
  })

  const observerRef = useRef<IntersectionObserver | null>(null)
  const lastPostRef = useRef<HTMLDivElement | null>(null)
  
  const buildFilters = useCallback((offset: number = 0): PostFilters => {
    // Check if all days are selected
    const allDaysSelected = Object.values(filters.dayOfWeek).every(day => day === true)
    
    // Check if all post types are selected
    const allTypesSelected = Object.values(filters.postTypes).every(type => type === true)
    
    return {
      query: searchQuery || undefined,
      date_from: filters.dateFrom || undefined,
      date_till: filters.dateTill || undefined,
      // Only include day of week params if not all days are selected
      day_of_week_mon: !allDaysSelected ? filters.dayOfWeek.mon : undefined,
      day_of_week_tue: !allDaysSelected ? filters.dayOfWeek.tue : undefined,
      day_of_week_wed: !allDaysSelected ? filters.dayOfWeek.wed : undefined,
      day_of_week_thu: !allDaysSelected ? filters.dayOfWeek.thu : undefined,
      day_of_week_fri: !allDaysSelected ? filters.dayOfWeek.fri : undefined,
      day_of_week_sat: !allDaysSelected ? filters.dayOfWeek.sat : undefined,
      day_of_week_sun: !allDaysSelected ? filters.dayOfWeek.sun : undefined,
      // Only include post type params if they are true AND not all are selected
      posts: !allTypesSelected && filters.postTypes.posts ? true : undefined,
      reposts: !allTypesSelected && filters.postTypes.reposts ? true : undefined,
      replays: !allTypesSelected && filters.postTypes.replays ? true : undefined,
      sort_by: sortBy === 'post_created_at' ? undefined : sortBy,
      sort_direction: sortDirection,
      offset,
      limit: POSTS_PER_PAGE,
    }
  }, [searchQuery, filters, sortBy, sortDirection])

  const loadPosts = useCallback(async (reset: boolean = false) => {
    if (loading) return

    setLoading(true)
    try {
      const offset = reset ? 0 : posts.length
      const newPosts = await getPosts(buildFilters(offset))
      
      if (reset) {
        setPosts(newPosts)
        setSelectedPosts(new Set())
      } else {
        setPosts(prev => [...prev, ...newPosts])
      }
      
      setHasMore(newPosts.length === POSTS_PER_PAGE)
    } catch (error) {
      console.error('Failed to load posts:', error)
    } finally {
      setLoading(false)
    }
  }, [loading, posts.length, buildFilters])

  // Reset and reload when filters change
  useEffect(() => {
    setPosts([])
    setSelectedPosts(new Set())
    setHasMore(true)
    loadPosts(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, filters, sortBy, sortDirection])

  // Infinite scroll
  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect()

    observerRef.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore && !loading) {
        loadPosts(false)
      }
    })

    if (lastPostRef.current) {
      observerRef.current.observe(lastPostRef.current)
    }

    return () => {
      if (observerRef.current) observerRef.current.disconnect()
    }
  }, [hasMore, loading, loadPosts])

  const handleSearch = () => {
    setSearchQuery(searchInput)
  }

  const handleApplyFilters = (newFilters: FilterState) => {
    setFilters(newFilters)
  }

  const handleSort = (newSortBy: SortBy) => {
    if (newSortBy === sortBy) {
      setSortDirection(prev => prev === 'desc' ? 'asc' : 'desc')
    } else {
      setSortBy(newSortBy)
      setSortDirection('desc')
    }
  }

  const togglePost = (id: number) => {
    const newSelected = new Set(selectedPosts)
    if (newSelected.has(id)) {
      newSelected.delete(id)
    } else {
      newSelected.add(id)
    }
    setSelectedPosts(newSelected)
  }
  
  const toggleAll = () => {
    if (selectedPosts.size === posts.length) {
      setSelectedPosts(new Set())
    } else {
      setSelectedPosts(new Set(posts.map(p => p.id)))
    }
  }

  const handleDeleteSelected = async () => {
    if (selectedPosts.size === 0) return
    
    const confirmed = window.confirm(`Delete ${selectedPosts.size} selected posts?`)
    if (!confirmed) return

    try {
      await deletePosts(Array.from(selectedPosts))
      await loadPosts(true)
    } catch (error) {
      console.error('Failed to delete posts:', error)
      alert('Failed to delete posts')
    }
  }

  const getPostTypeLabel = (post: Post): string => {
    switch (post.post_type) {
      case 'post': return 'You posted'
      case 'repost': return 'You reposted'
      case 'reply': return 'You replied 💬'
      default: return 'You posted'
    }
  }

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString)
    return date.toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    })
  }

  const getSortLabel = (): string => {
    const labels: Record<SortBy, string> = {
      post_created_at: 'Newest first',
      reply_count: 'Most replies',
      repost_count: 'Most reposts',
      like_count: 'Most likes',
      quote_count: 'Most quotes',
    }
    return labels[sortBy]
  }

  const getActiveFilters = (): { label: string; onRemove: () => void }[] => {
    const activeFilters: { label: string; onRemove: () => void }[] = []

    // Post types
    const allTypesSelected = Object.values(filters.postTypes).every(v => v)
    if (!allTypesSelected) {
      const selectedTypes = Object.entries(filters.postTypes)
        .filter(([_, selected]) => selected)
        .map(([type]) => type === 'posts' ? 'Skeets' : type === 'reposts' ? 'Reposts' : 'Replies')
      
      if (selectedTypes.length > 0) {
        activeFilters.push({
          label: selectedTypes.join(', '),
          onRemove: () => setFilters(prev => ({
            ...prev,
            postTypes: { posts: true, reposts: true, replays: true }
          }))
        })
      }
    }

    // Date range
    if (filters.dateFrom || filters.dateTill) {
      const dateLabel = filters.dateFrom && filters.dateTill
        ? `${filters.dateFrom} - ${filters.dateTill}`
        : filters.dateFrom
        ? `From ${filters.dateFrom}`
        : `Until ${filters.dateTill}`
      
      activeFilters.push({
        label: `Date: ${dateLabel}`,
        onRemove: () => setFilters(prev => ({ ...prev, dateFrom: '', dateTill: '' }))
      })
    }

    // Day of week
    const allDaysSelected = Object.values(filters.dayOfWeek).every(v => v)
    if (!allDaysSelected) {
      const selectedDays = Object.entries(filters.dayOfWeek)
        .filter(([_, selected]) => selected)
        .map(([day]) => day.charAt(0).toUpperCase() + day.slice(1))
      
      if (selectedDays.length > 0) {
        activeFilters.push({
          label: selectedDays.join(', '),
          onRemove: () => setFilters(prev => ({
            ...prev,
            dayOfWeek: { mon: true, tue: true, wed: true, thu: true, fri: true, sat: true, sun: true }
          }))
        })
      }
    }

    return activeFilters
  }
  
  return (
    <>
      <FilterModal
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        onApply={handleApplyFilters}
        initialFilters={filters}
      />
      
      <Container centered>
        {/* Search Bar */}
        <Card padding="2rem" style={{ marginBottom: '2rem' }}>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Input
              type="text"
              placeholder="Search by post / skeet keyword"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              style={{ flex: 1 }}
            />
            <Button 
              onClick={handleSearch}
              variant="primary"
              style={{
                backgroundColor: 'var(--color-primary)',
                color: 'white',
                padding: '0.75rem 2rem',
                borderRadius: '0.375rem'
              }}
            >
              <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24" style={{ display: 'inline' }}>
                <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
              </svg>
            </Button>
          </div>
          
          {/* Filters */}
          <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <Button 
              onClick={() => setIsFilterOpen(true)}
              variant="ghost"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                color: 'var(--color-primary)',
                fontWeight: '400',
                border: 'none',
                backgroundColor: 'transparent',
                padding: '0.5rem',
                borderRadius: '0.375rem'
              }}
            >
              <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z"/>
              </svg>
              Filter
            </Button>
            
            {/* Active Filter Pills */}
            {getActiveFilters().map((filter, index) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 1rem',
                  backgroundColor: 'var(--blue-200)',
                  border: '1px solid var(--color-primary)',
                  borderRadius: '2rem',
                  fontSize: '0.875rem',
                  color: 'var(--color-text)'
                }}
              >
                <span>{filter.label}</span>
                <button
                  onClick={filter.onRemove}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '0',
                    display: 'flex',
                    alignItems: 'center',
                    color: 'var(--color-text-secondary)'
                  }}
                >
                  <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                  </svg>
                </button>
              </div>
            ))}
            
            {selectedPosts.size > 0 && (
              <Button 
                onClick={handleDeleteSelected}
                variant="primary"
                style={{
                  backgroundColor: 'var(--red-500)',
                  color: 'white',
                  borderRadius: '0.375rem',
                  marginLeft: 'auto'
                }}
              >
                Delete {selectedPosts.size} selected
              </Button>
            )}
          </div>
        </Card>
      
      {/* Posts Table */}
      <Card padding="0" style={{ overflow: 'hidden' }}>
        {/* Table Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '1rem 1.5rem',
          borderBottom: '1px solid var(--color-border)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="checkbox"
              checked={posts.length > 0 && selectedPosts.size === posts.length}
              onChange={toggleAll}
              disabled={posts.length === 0}
              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
            />
            <span style={{ color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}>
              Showing {posts.length} {hasMore ? '+' : ''} entries
            </span>
          </div>
          
          <div style={{ position: 'relative' }}>
            <button 
              onClick={() => {
                const options: SortBy[] = ['post_created_at', 'reply_count', 'repost_count', 'like_count', 'quote_count']
                const currentIndex = options.indexOf(sortBy)
                const nextIndex = (currentIndex + 1) % options.length
                handleSort(options[nextIndex])
              }}
              style={{
                padding: '0.5rem 1rem',
                border: '1px solid var(--color-border)',
                borderRadius: '0.375rem',
                backgroundColor: 'white',
                cursor: 'pointer',
                color: 'var(--color-text)',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <strong>{getSortLabel()}</strong>
              <svg 
                width="16" 
                height="16" 
                fill="currentColor" 
                viewBox="0 0 24 24"
                style={{ transform: sortDirection === 'asc' ? 'rotate(180deg)' : 'none' }}
              >
                <path d="M7 10l5 5 5-5z"/>
              </svg>
            </button>
          </div>
        </div>
        
        {/* Posts List */}
        {loading && posts.length === 0 ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--color-text-secondary)' }}>
            Loading posts...
          </div>
        ) : posts.length === 0 ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--color-text-secondary)' }}>
            No posts found
          </div>
        ) : (
          <>
            {posts.map((post, index) => (
              <div
                key={post.id}
                ref={index === posts.length - 1 ? lastPostRef : null}
                style={{
                  padding: '1.5rem',
                  borderBottom: index < posts.length - 1 ? '1px solid var(--color-border)' : 'none',
                  display: 'grid',
                  gridTemplateColumns: 'auto 1fr auto',
                  gap: '1rem',
                  alignItems: 'start',
                  opacity: post.marked_for_destruction ? 0.5 : 1,
                  transition: 'opacity 0.2s'
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedPosts.has(post.id)}
                  onChange={() => togglePost(post.id)}
                  disabled={post.marked_for_destruction}
                  style={{ 
                    width: '18px', 
                    height: '18px', 
                    cursor: post.marked_for_destruction ? 'not-allowed' : 'pointer', 
                    marginTop: '0.25rem'
                  }}
                />
                
                <div>
                  <div style={{ 
                    fontSize: '0.875rem', 
                    color: 'var(--color-text-secondary)',
                    marginBottom: '0.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    {getPostTypeLabel(post)}
                    {post.marked_for_destruction && (
                      <span style={{
                        fontSize: '0.75rem',
                        padding: '0.125rem 0.5rem',
                        backgroundColor: 'var(--red-200)',
                        color: 'var(--red-500)',
                        borderRadius: '0.25rem',
                        fontWeight: '600'
                      }}>
                        Marked for deletion
                      </span>
                    )}
                  </div>
                  <div style={{ 
                    fontSize: '1rem',
                    color: 'var(--color-text)',
                    lineHeight: '1.5',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word'
                  }}>
                    {post.text}
                  </div>
                </div>
                
                <div style={{ 
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-end',
                  gap: '0.5rem'
                }}>
                  <div style={{ 
                    display: 'flex',
                    gap: '1rem',
                    fontSize: '0.875rem',
                    color: 'var(--color-text-secondary)'
                  }}>
                    <span title="Replies">💬 {post.reply_count}</span>
                    <span title="Reposts">🔄 {post.repost_count}</span>
                    <span title="Likes">❤️ {post.like_count}</span>
                    <span title="Quotes">💭 {post.quote_count}</span>
                  </div>
                  <div style={{ 
                    fontSize: '0.875rem',
                    color: 'var(--color-text-secondary)'
                  }}>
                    {formatDate(post.post_created_at)}
                  </div>
                </div>
              </div>
            ))}
            
            {loading && posts.length > 0 && (
              <div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--color-text-secondary)' }}>
                Loading more...
              </div>
            )}
          </>
        )}
      </Card>
      </Container>
    </>
  )
}

